import { useMemo } from 'react';
import { css } from '@emotion/react';
import { DataSource } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/datasource.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { DropdownFilterChipField, useForm } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../../hooks';
import { TableFilters } from '.';

const UNASSIGNED_OPTION = { label: 'Unassigned', value: 'unassigned' };

export const TableCustomFilters = ({
  dataSourcesData,
  setFilters,
}: {
  dataSourcesData: DataSource[];
  setFilters: (filters: TableFilters) => void;
}) => {
  const { t } = useTranslation('scheduleGlobalSettings');
  const { selectedLocationIds, getLocationName } = useSchedulingLocationInfo();

  const locationOptions = useMemo(() => {
    const selectedLocationOptions = selectedLocationIds.map((locationId) => ({
      label: getLocationName(locationId),
      value: locationId,
    }));
    return [...selectedLocationOptions, UNASSIGNED_OPTION];
  }, [selectedLocationIds, getLocationName]);

  const sourceOptions = useMemo(
    () =>
      dataSourcesData.map((dataSource) => ({
        label: dataSource.sourceName,
        value: dataSource.sourceId,
      })),
    [dataSourcesData]
  );

  const { getFieldProps } = useForm({
    fields: {
      location: {
        type: 'checklist',
        required: true,
        value: [],
      },
      source: {
        type: 'checklist',
        required: true,
        value: [],
      },
    },
  });

  const handleApplyFilters = (field: 'location' | 'source', value: string[]) => {
    const currentLocationValue = field === 'location' ? value : getFieldProps('location').value;
    const currentSourceValue = field === 'source' ? value : getFieldProps('source').value;

    const locationIds = currentLocationValue.filter((id: string) => id !== 'unassigned');
    const sourceIds = currentSourceValue;
    const isUnassignedSelected = currentLocationValue.includes('unassigned');

    setFilters({ location: locationIds, source: sourceIds, unassigned: isUnassignedSelected });
  };

  return (
    <div css={containerStyles}>
      <DropdownFilterChipField
        {...getFieldProps('location')}
        label={t('Location')}
        icon='location-small'
        getCustomLabel={(items) => (items.length > 1 ? `${items.length} Locations` : items[0])}
        onApply={({ close, value }) => {
          handleApplyFilters('location', value);
          close();
        }}
        showClearAll={false}
        showSelectAll={true}
        showSearchBar={true}
      >
        {locationOptions.map(({ label, value }) => (
          <DropdownFilterChipField.Option key={value} value={value}>
            {label}
          </DropdownFilterChipField.Option>
        ))}
      </DropdownFilterChipField>
      <DropdownFilterChipField
        {...getFieldProps('source')}
        icon='network-small'
        label={t('Source')}
        getCustomLabel={(items) => (items.length > 1 ? `${items.length} Sources` : items[0])}
        onApply={({ close, value }) => {
          handleApplyFilters('source', value);
          close();
        }}
        showClearAll={false}
        showSelectAll={true}
        showSearchBar={true}
      >
        {sourceOptions.map(({ label, value = '' }) => (
          <DropdownFilterChipField.Option key={value} value={value}>
            {label}
          </DropdownFilterChipField.Option>
        ))}
      </DropdownFilterChipField>
    </div>
  );
};

const containerStyles = css({
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
});
